import React, { useRef, useState } from "react";
import { Box, Typography, Paper, Chip, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import type { Swiper as SwiperInstance } from "swiper";
import "swiper/css";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { Button } from "./Buttons";
import { HeadingGradient, SmallTextGradient, IconGradient } from "./Gradient";

type QuestStatus = "upcoming" | "ongoing" | "completed";

const STATUS_LABELS: Record<QuestStatus, string> = {
  upcoming: "Upcoming",
  ongoing: "Ongoing",
  completed: "Completed",
};

const ContestBanner = ({ quests }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const swiperRef = useRef<SwiperInstance | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  if (!quests || quests.length === 0) return null;

  // Sort quests: ongoing first, upcoming second, completed third
  // Status comes from backend (attached in HomePage)
  const sortedQuests = [...quests].sort((a, b) => {
    const statusA = (a.status as QuestStatus) || "ongoing";
    const statusB = (b.status as QuestStatus) || "ongoing";
    const statusOrder: Record<QuestStatus, number> = {
      ongoing: 0,
      upcoming: 1,
      completed: 2,
    };
    return statusOrder[statusA] - statusOrder[statusB];
  });

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    });
  };

  const handleClick = (id: string) => {
    navigate(`/challenges/ink-masters/${id}`);
  };

  return (
    <Box
      sx={{
        position: "relative",
        mb: { xs: 4, sm: 5, md: 6 },
        width: "100%",
        overflow: "hidden",
      }}
    >
      {/* Compact Swiper Container */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          "& .swiper": {
            overflow: "visible",
            width: "100%",
          },
          "& .swiper-wrapper": {
            width: "100%",
          },
          "& .swiper-slide": {
            width: "100%",
            maxWidth: "100%",
          },
        }}
      >
        <Swiper
          modules={[Autoplay]}
          slidesPerView={1}
          loop={sortedQuests.length > 1}
          speed={600}
          autoplay={sortedQuests.length > 1 ? { delay: 4000, pauseOnMouseEnter: true } : false}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          onSlideChange={(swiper) => {
            setActiveIndex(swiper.realIndex);
          }}
        >
          {sortedQuests.map((quest) => {
            // Use status from backend, fallback to 'ongoing' if not provided
            const status = (quest.status as QuestStatus) || "ongoing";
            // Calculate total prize: sum of all rewards (1st, 2nd, 3rd prize)
            const totalPrize = quest.rewards?.reduce((sum, reward) => {
              return sum + (Number(reward.amount) || 0);
            }, 0) || 0;
            return (
            <SwiperSlide key={quest.id}>
              <Paper
                elevation={0}
                onClick={() => handleClick(quest.id)}
                sx={{
                  position: "relative",
                  p: { xs: 2, sm: 3, md: 3.5 },
                  width: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  borderRadius: 3,
                    backgroundColor: "transparent",
                    backdropFilter: "blur(12px)",
                    border: (theme) =>
                      `1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.08)"
                      }`,
                    boxShadow: (theme) =>
                      theme.palette.mode === "dark"
                        ? "0 2px 8px rgba(0,0,0,0.1)"
                        : "0 2px 8px rgba(0,0,0,0.05)",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: (theme) =>
                        theme.palette.mode === "dark"
                          ? "0 4px 16px rgba(0,0,0,0.2)"
                          : "0 4px 16px rgba(0,0,0,0.1)",
                      border: (theme) =>
                        `1px solid ${
                          theme.palette.mode === "dark"
                            ? "rgba(165, 180, 252, 0.2)"
                            : "rgba(59, 130, 246, 0.2)"
                        }`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: { xs: 1.5, sm: 3 },
                      width: "100%",
                      minWidth: 0,
                    }}
                  >
                    {/* Trophy Icon */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: { xs: 48, sm: 56 },
                        height: { xs: 48, sm: 56 },
                        borderRadius: 2,
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(165, 180, 252, 0.1)"
                            : "rgba(59, 130, 246, 0.1)",
                        border: (theme) =>
                          `1px solid ${
                            theme.palette.mode === "dark"
                              ? "rgba(165, 180, 252, 0.2)"
                              : "rgba(59, 130, 246, 0.2)"
                          }`,
                        flexShrink: 0,
                      }}
                    >
                      <IconGradient gradientVariant="default">
                        <EmojiEventsIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
                      </IconGradient>
                    </Box>

                    {/* Content */}
                    <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                          flexWrap: "wrap",
                          width: "100%",
                        }}
                      >
                        <SmallTextGradient
                          sx={{
                            fontSize: { xs: "0.6875rem", sm: "0.75rem" },
                            fontWeight: 700,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          Ink Masters Challenge
                        </SmallTextGradient>
                        <Chip
                          label={STATUS_LABELS[status]}
                          size="small"
                          sx={(theme) => {
                            const isDark = theme.palette.mode === "dark";
                            const styles: Record<QuestStatus, { bg: string; border: string; color: string }> = {
                              upcoming: {
                                bg: isDark ? "rgba(129,140,248,0.18)" : "rgba(59,130,246,0.12)",
                                border: isDark ? "rgba(129,140,248,0.45)" : "rgba(59,130,246,0.35)",
                                color: isDark ? "#c7d2fe" : "#1d4ed8",
                              },
                              ongoing: {
                                bg: isDark ? "rgba(74,222,128,0.18)" : "rgba(34,197,94,0.12)",
                                border: isDark ? "rgba(74,222,128,0.5)" : "rgba(34,197,94,0.35)",
                                color: isDark ? "#bbf7d0" : "#15803d",
                              },
                              completed: {
                                bg: isDark ? "rgba(148,163,184,0.16)" : "rgba(148,163,184,0.16)",
                                border: isDark ? "rgba(148,163,184,0.4)" : "rgba(148,163,184,0.4)",
                                color: isDark ? "#e2e8f0" : "#475569",
                              },
                            };
                            return {
                              height: { xs: 22, sm: 24 },
                              px: { xs: 1, sm: 1.25 },
                              borderRadius: 999,
                              fontWeight: 600,
                              fontSize: { xs: "0.625rem", sm: "0.6875rem" },
                              letterSpacing: { xs: "0.04em", sm: "0.05em" },
                              textTransform: "uppercase",
                              backgroundColor: styles[status].bg,
                              border: `1px solid ${styles[status].border}`,
                              color: styles[status].color,
                              lineHeight: 1,
                            };
                          }}
                        />
                      </Box>
                      <HeadingGradient
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
                          mb: 1,
                          lineHeight: 1.3,
                          display: "-webkit-box",
                          overflow: "hidden",
                          WebkitLineClamp: { xs: 2, sm: 1 },
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {quest.title}
                      </HeadingGradient>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: { xs: 1, sm: 1.5 },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: { xs: "0.6875rem", sm: "0.75rem" },
                            color: "text.secondary",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <CalendarTodayIcon sx={{ fontSize: 14 }} />
                          {formatDate(quest.period.start)} - {formatDate(quest.period.end)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: { xs: "0.6875rem", sm: "0.75rem" },
                            color: "text.secondary",
                            fontWeight: 600,
                          }}
                        >
                          • Total Prize: ₹{totalPrize.toLocaleString("en-IN")}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Action Button - Right Side */}
                    <Box sx={{ flexShrink: 0, alignSelf: "center", ml: { xs: 0.5, sm: 1 } }}>
                      <Button
                        variant={isMobile ? "text" : "outlined"}
                        size="small"
                        sx={{
                          minWidth: 0,
                          px: { xs: 1.5, sm: 2.5 },
                          whiteSpace: "nowrap",
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        }}
                      >
                        View →
                      </Button>
                    </Box>
                  </Box>
                </Paper>
            </SwiperSlide>
          );
          })}
        </Swiper>
        {sortedQuests.length > 1 && (
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "center",
              gap: 1,
            }}
          >
            {sortedQuests.map((quest, index) => (
              <Box
                key={quest.id}
                role="button"
                aria-label={`Go to ${quest.title}`}
                tabIndex={0}
                onClick={() => swiperRef.current?.slideToLoop(index)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    swiperRef.current?.slideToLoop(index);
                  }
                }}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  cursor: "pointer",
                  backgroundColor: (theme) =>
                    index === activeIndex
                      ? theme.palette.mode === "dark"
                        ? "#a5b4fc"
                        : "#3b82f6"
                      : "transparent",
                  border: (theme) =>
                    `1px solid ${
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(0,0,0,0.3)"
                    }`,
                  transition: "all 0.2s ease",
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ContestBanner;
